import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";

/* Hand-stitch dashed rule — the recurring signature motif */
const Stitch = ({ className = "", color = "#2E7D32" }) => (
  <svg className={className} viewBox="0 0 100 4" preserveAspectRatio="none">
    <line
      x1="0" y1="2" x2="100" y2="2"
      stroke={color} strokeWidth="2" strokeDasharray="6 5" strokeLinecap="round"
    />
  </svg>
);

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const navLinks = [
    { name: "Home", href: "#home" },
    { name: "About", href: "#about" },
    { name: "Programs", href: "#programs" },
    { name: "Impact", href: "#impact" },
    { name: "Contact", href: "#contact" },
  ];

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#FBF9F3]/92 backdrop-blur-xl shadow-[0_1px_0_0_rgba(27,42,74,0.08),0_10px_28px_-16px_rgba(27,42,74,0.35)]"
          : "bg-[#FBF9F3]/70 backdrop-blur-md"
      }`}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,700&family=IBM+Plex+Sans:wght@400;500;600&display=swap');
        .mbf-display { font-family: 'Fraunces', serif; }
        .mbf-body { font-family: 'IBM Plex Sans', sans-serif; }
      `}</style>

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-[76px] md:h-[84px]">
          {/* Logo */}
          <a href="#home" className="flex items-center gap-3 group shrink-0">
            <img
              src="/logo.png"
              alt="MB Foundation"
              className="h-9 md:h-10 w-auto"
              onError={(e) => { e.target.style.display = "none"; }}
            />
            <div className="leading-tight mbf-body">
              <h1 className="mbf-display text-[16px] md:text-[18px] font-semibold text-[#0A355C] tracking-tight">
                MB Foundation
              </h1>
              <p className="text-[10.5px] md:text-[11px] text-[#6b6355] font-medium tracking-wide uppercase">
                Skill Development &amp; Livelihood
              </p>
            </div>
          </a>

          {/* Desktop Menu */}
          <nav className="hidden md:flex items-center gap-1 mbf-body">
            {navLinks.map((item) => (
              <a
                key={item.name}
                href={item.href}
                className="relative px-4 py-2 text-[14.5px] text-[#40392f] hover:text-[#0A355C] font-medium transition-colors group"
              >
                {item.name}
                <Stitch
                  className="absolute left-4 right-4 -bottom-[1px] w-[calc(100%-2rem)] h-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  color="#2E7D32"
                />
              </a>
            ))}
          </nav>

          {/* CTA — tag-shaped button */}
          <div className="hidden md:block shrink-0">
            <a
              href="#contact"
              className="relative inline-flex items-center gap-2 pl-6 pr-5 py-[10px] mbf-body text-[13.5px] font-semibold text-[#0A355C] transition-transform duration-200 hover:-translate-y-0.5"
              style={{
                background: "#E0A526",
                clipPath: "polygon(14px 0, 100% 0, 100% 100%, 14px 100%, 0 50%)",
              }}
            >
              <span className="absolute left-[7px] top-1/2 -translate-y-1/2 w-[6px] h-[6px] rounded-full bg-[#0A355C]/70 ring-2 ring-[#E0A526]" />
              Get Involved
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden relative w-10 h-10 flex items-center justify-center rounded-full hover:bg-[#0A355C]/5 transition-colors"
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle menu"
            aria-expanded={isOpen}
          >
            <X
              className={`w-5 h-5 text-[#0A355C] absolute transition-all duration-300 ${
                isOpen ? "opacity-100 rotate-0 scale-100" : "opacity-0 rotate-45 scale-75"
              }`}
            />
            <Menu
              className={`w-5 h-5 text-[#0A355C] absolute transition-all duration-300 ${
                isOpen ? "opacity-0 -rotate-45 scale-75" : "opacity-100 rotate-0 scale-100"
              }`}
            />
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <div
        className={`md:hidden fixed inset-0 top-[76px] bg-[#082440]/40 backdrop-blur-sm transition-opacity duration-300 ${
          isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setIsOpen(false)}
      />

      {/* Mobile Menu Panel */}
      <div
        className={`md:hidden fixed top-[76px] left-0 right-0 bg-[#FBF9F3] shadow-2xl overflow-hidden transition-all duration-300 ease-out ${
          isOpen ? "max-h-[30rem] opacity-100" : "max-h-0 opacity-0"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <Stitch className="w-full h-2" color="#2E7D32" />
        <div className="flex flex-col px-6 py-6 space-y-1 mbf-body">
          {navLinks.map((item, i) => (
            <a
              key={item.name}
              href={item.href}
              className="text-[#40392f] font-medium text-[15px] py-3 px-3 rounded-lg hover:bg-[#0A355C]/5 hover:text-[#0A355C] transition-colors"
              style={{ transitionDelay: isOpen ? `${i * 30}ms` : "0ms" }}
              onClick={() => setIsOpen(false)}
            >
              {item.name}
            </a>
          ))}

          <a
            href="#contact"
            className="relative mt-3 inline-flex justify-center items-center pl-6 pr-5 py-3.5 text-[#0A355C] text-center font-semibold"
            style={{
              background: "#E0A526",
              clipPath: "polygon(14px 0, 100% 0, 100% 100%, 14px 100%, 0 50%)",
            }}
            onClick={() => setIsOpen(false)}
          >
            Get Involved
          </a>
        </div>
      </div>
    </header>
  );
};

export default Navbar;